import React, { useMemo, useRef, useCallback } from 'react';
import { useDiary, pixelY, formatTime } from './DiaryContext';
import { useDrag } from './DragContext';
import { BlockIcon, CardIcon } from '../../assets/SVG';
import { matchEventEmoji } from './eventIllustrations';
import { getTextColour } from '../shared/ColourPicker';
import { useHighlight, matchesHighlight } from './diaryHighlight';
import { refundBadge, refundStatusOf } from '../refunds/refundLogic';

// "Refunded" / "Part refunded" mark on a completed booking's card.
const REFUND_MARK_STYLE = {
  display: 'inline-flex', alignItems: 'center', marginLeft: '4px', padding: '0 5px', borderRadius: '4px',
  fontSize: '9px', fontWeight: 700, lineHeight: '14px', letterSpacing: '0.02em', textTransform: 'uppercase',
  background: 'rgba(255, 255, 255, 0.85)', color: '#6b7280', whiteSpace: 'nowrap', flexShrink: 0,
};

// Mouse: minimum px before drag starts (no delay)
const DRAG_THRESHOLD = 6;
// Touch: hold time before drag becomes available (ms)
const TOUCH_HOLD_MS = 500;
// Touch: movement that cancels the hold (px) - allows scroll
const TOUCH_CANCEL_PX = 10;

/**
 * EventBlock - A single booking/event/break/holiday card.
 *
 * Absolutely positioned within its StaffColumn.
 * Width determined by overlap lane assignment.
 * Content density adapts to available height.
 */
function EventBlock({ event, lane, laneCount, overflow, hidden, resizeStartMinutes, resizeEndMinutes }) {
  const { state, onEventClick, onEventContextMenu } = useDiary();
  const { viewStartMinutes, zoomLevel } = state;
  const { dragState, startDrag, startResize, activatedEventId, activateEvent, deactivateEvent } = useDrag();
  const landed = matchesHighlight(useHighlight(), event);
  const isActivated = activatedEventId === event.id;
  const pointerStart = useRef(null);
  const touchClickedAt = useRef(0);
  const holdTimerRef = useRef(null);

  const isDragging = dragState && dragState.mode === 'move' && dragState.event.id === event.id;
  const isResizing = dragState && dragState.mode === 'resize' && dragState.event.id === event.id;

  const handlePointerDown = useCallback((e) => {
    if (!event.editable) return;
    if (e.button && e.button !== 0) return;
    e.stopPropagation();

    const isTouch = !!e.touches;
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    const targetEl = e.currentTarget;
    pointerStart.current = { x: clientX, y: clientY, moved: false };

    if (isTouch) {
      // ── Touch path: long-press then drag or activate ──
      let dragReady = false;

      const cleanup = () => {
        if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
      };

      // Start hold timer
      holdTimerRef.current = setTimeout(() => {
        dragReady = true;
        activateEvent(event.id);
        if (navigator.vibrate) navigator.vibrate(10);
      }, TOUCH_HOLD_MS);

      const onTouchMove = (ev) => {
        const cx = ev.touches[0].clientX;
        const cy = ev.touches[0].clientY;
        const dx = cx - pointerStart.current.x;
        const dy = cy - pointerStart.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (!dragReady) {
          if (dist > TOUCH_CANCEL_PX) {
            cleanup();
          }
          return;
        }

        // Hold completed, now in drag mode
        if (ev.cancelable) ev.preventDefault();
        if (!pointerStart.current.moved && dist >= DRAG_THRESHOLD) {
          pointerStart.current.moved = true;
          deactivateEvent();
          const rect = targetEl.getBoundingClientRect();
          const offsetY = pointerStart.current.y - rect.top;
          if (event.laneReadOnly) return; // v1 room lanes: no drag — reassignment rides the booking panel
          startDrag(event, event.staffId, cx, cy, offsetY);
          cleanup();
        }
      };

      const onTouchEnd = (ev) => {
        cleanup();
        if (!pointerStart.current?.moved) {
          if (dragReady) {
            // Hold completed but no drag - stay activated for resize handles
            ev.preventDefault();
          } else {
            // Normal tap - trigger click
            touchClickedAt.current = Date.now();
            ev.preventDefault();
            if (onEventClick) onEventClick(event);
          }
        }
        pointerStart.current = null;
      };

      document.addEventListener('touchmove', onTouchMove, { passive: false });
      document.addEventListener('touchend', onTouchEnd);

    } else {
      // ── Mouse path: immediate 6px threshold (unchanged) ──
      const cleanup = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      const onMouseMove = (ev) => {
        if (!pointerStart.current) return;
        const cx = ev.clientX;
        const cy = ev.clientY;
        const dx = cx - pointerStart.current.x;
        const dy = cy - pointerStart.current.y;

        if (!pointerStart.current.moved && Math.sqrt(dx * dx + dy * dy) >= DRAG_THRESHOLD) {
          pointerStart.current.moved = true;
          const rect = targetEl.getBoundingClientRect();
          const offsetY = pointerStart.current.y - rect.top;
          if (event.laneReadOnly) return; // v1 room lanes: no drag — reassignment rides the booking panel
          startDrag(event, event.staffId, cx, cy, offsetY);
          cleanup();
        }
      };

      const onMouseUp = () => {
        cleanup();
        if (pointerStart.current && !pointerStart.current.moved) {
          // The browser's click follows this mouseup; handleClick skips it
          touchClickedAt.current = Date.now();
          if (onEventClick) onEventClick(event);
        }
        pointerStart.current = null;
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }
  }, [event, startDrag, onEventClick, activateEvent, deactivateEvent]);

  const handleResizeDown = useCallback((e) => {
    if (!event.editable || !event.resizable) return;
    // On touch, only allow resize when activated (persistent hold-ready)
    if (e.touches && activatedEventId !== event.id) return;
    e.stopPropagation();
    e.preventDefault();

    const edge = e.currentTarget.dataset.edge || 'bottom';
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    startResize(event, clientY, edge);
  }, [event, startResize, activatedEventId]);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEventContextMenu) {
      onEventContextMenu(event, e.clientX, e.clientY);
    }
  }, [event, onEventContextMenu]);

  const handleClick = (e) => {
    e.stopPropagation();
    // Deactivate any activated event on click
    if (activatedEventId) deactivateEvent();
    // Suppress click that fires after a mouse drag. The browser dispatches a
    // synthetic click event after mouseup on the same element — without this
    // guard, DiaryV2.handleEventClick fires post-drop and overwrites the
    // selectedEvent (clobbering _dropOverrides set by handleBookingDrop), so
    // BookingDetailPanel.handleDropEdit reads `hasDropOverrides: false` and
    // falls through to handleEdit with the optimistically-moved event.
    if (pointerStart.current?.moved) {
      pointerStart.current = null;
      return;
    }
    // touchend / mouseup already fired onEventClick. The browser then sends a
    // click (~300ms later on touch) — ignore it so the click isn't handled
    // twice (the panel opening then closing, or every toast shown twice).
    if (Date.now() - touchClickedAt.current < 500) return;
    if (onEventClick) onEventClick(event);
  };

  const effectiveStart = resizeStartMinutes != null ? resizeStartMinutes : event.startMinutes;
  const effectiveEnd = resizeEndMinutes != null ? resizeEndMinutes : event.endMinutes;
  const top = pixelY(effectiveStart, viewStartMinutes, zoomLevel);
  const height = pixelY(effectiveEnd, viewStartMinutes, zoomLevel) - top;
  // Density modes based on pixel height
  const density = useMemo(() => {
    if (height >= 100) return 'full';
    if (height >= 60) return 'standard';
    if (height >= 22) return 'compact';
    return 'micro';
  }, [height]);

  // Lane positioning
  const gap = 2;
  const widthPercent = 100 / laneCount;
  const leftPercent = lane * widthPercent;

  const bg = event.diaryColour || '#D6D5EC';
  const isDarkBg = getTextColour(bg) === 'light';
  const style = {
    top,
    height,
    left: `calc(${leftPercent}% + ${lane > 0 ? gap / 2 : 0}px)`,
    width: `calc(${widthPercent}% - ${gap}px)`,
    backgroundColor: bg,
    opacity: (isDragging || isResizing) ? 0.3 : undefined,
    cursor: event.editable ? 'grab' : undefined,
  };

  // Status flags
  const isComplete = event.status === 4 || event.status === 6;
  const isBlocked = event.status === 6;
  const hasCard = event.stripePaymentMethodId && event.stripePaymentMethodId !== '';
  const refundMark = event.type === 'booking' && event.status === 4 ? refundBadge(refundStatusOf(event)) : null;

  if (hidden) return null;

  // ── Booking Card ──
  if (event.type === 'booking') {
    // Setup / cleanup hatched overlays sized as a fraction of the block height.
    // We compute pct from minutes so they stay accurate when the block is
    // resized live (drag handles update effectiveStart/End).
    const totalMinutes = Math.max(1, effectiveEnd - effectiveStart);
    const setupPct = event.setupMinutes ? Math.min(100, (event.setupMinutes / totalMinutes) * 100) : 0;
    const cleanupPct = event.cleanupMinutes ? Math.min(100, (event.cleanupMinutes / totalMinutes) * 100) : 0;
    // Pixel offsets so we can push the title/time content out of the
    // hatched buffer regions (otherwise it overlaps the setup hatch at the
    // top, which is what's drawn in the screenshot).
    const setupPx = event.setupMinutes ? Math.round((event.setupMinutes / totalMinutes) * height) : 0;
    const cleanupPx = event.cleanupMinutes ? Math.round((event.cleanupMinutes / totalMinutes) * height) : 0;

    return (
      <div
        data-booking-id={event.bookingId || undefined}
        className={`dv2-event-block dv2-event--booking ${landed ? 'dv2-event--landed' : ''} ${event.redacted ? 'dv2-event--redacted' : ''} ${isComplete ? 'dv2-event--complete' : ''} ${isActivated ? 'dv2-event--hold-ready' : ''} dv2-event--${density} ${isDarkBg ? 'dv2-event--dark-bg' : ''} ${laneCount > 1 ? 'dv2-event--narrow' : ''}`}
        style={style}
        title={event.redacted ? 'Busy' : `${event.clientName} - ${event.title}`}
        onClick={event.redacted ? undefined : handleClick}
        onContextMenu={handleContextMenu}
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
      >
        <div className="dv2-event-color-strip" style={{ backgroundColor: event.diaryColour }} />
        {/* Setup / cleanup buffer overlays. Hatched pattern, pointer-events
            disabled so they don't break drag/click. Rendered before content
            so the title and time still sit on top. */}
        {setupPct > 0 && (
          <div className="dv2-event-buffer dv2-event-buffer--setup" style={{ height: `${setupPct}%` }} />
        )}
        {cleanupPct > 0 && (
          <div className="dv2-event-buffer dv2-event-buffer--cleanup" style={{ height: `${cleanupPct}%` }} />
        )}
        <div
          className="dv2-event-content"
          style={(setupPx || cleanupPx) ? {
            // Push title/time out of the hatched setup/cleanup regions so
            // they sit in the actual service area of the block. The +8px
            // gives a bit of breathing room between the hatch line and
            // the first / last line of text.
            paddingTop: setupPx ? `${setupPx + 8}px` : undefined,
            paddingBottom: cleanupPx ? `${cleanupPx + 8}px` : undefined,
            justifyContent: 'flex-start',
          } : undefined}
        >
          {density === 'micro' ? (
            <span className="dv2-event-title dv2-event-title--micro">{event.clientName || '?'}</span>
          ) : (
            <>
              <div className="dv2-event-header">
                {density === 'compact' && isBlocked && <span className="dv2-event-icon-sm"><BlockIcon /></span>}
                {density === 'compact' && !isBlocked && hasCard && <span className="dv2-event-icon-sm"><CardIcon /></span>}
                <span className="dv2-event-title">
                  {event.redacted ? 'Busy' : <>{event.clientName}{density !== 'compact' ? `, ${event.title}` : ''}</>}
                </span>
              </div>
              {/* Room lane: whose booking holds this room (R7: absent when redacted) */}
              {event.isResourceLane && event.laneStylistName && !event.redacted && (
                <div className="dv2-event-res-stylist">{event.laneStylistName}</div>
              )}
              {/* Staff lane: which room(s) this booking holds */}
              {!event.isResourceLane && Array.isArray(event.resources) && event.resources.length > 0 && density !== 'micro' && (
                <div className="dv2-event-res-badges">
                  {event.resources.map((r, ri) => (
                    <span className="dv2-event-res-badge" key={ri}>
                      <span className="dv2-event-res-badge-dot" style={{ background: r.diaryColour || '#8B8B93' }} />
                      {r.resourceName}
                    </span>
                  ))}
                </div>
              )}
              {density === 'full' && event.clientAvatar && event.clientId !== '64f5c7789a069cda0188fea3' && (
                <img
                  className="dv2-event-avatar"
                  src={event.clientAvatar}
                  alt=""
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="dv2-event-time">
                {(density === 'full' || density === 'standard') && (
                  <>
                    {isBlocked && <span className="dv2-event-icon"><BlockIcon /></span>}
                    {!isBlocked && hasCard && <span className="dv2-event-icon"><CardIcon /></span>}
                  </>
                )}
                <span>{formatTime(effectiveStart)}-{formatTime(effectiveEnd)}</span>
                {event.type === 'booking' && event.seriesId && density !== 'micro' && (
                  <span className="dv2-event-icon" title="Repeat booking" aria-label="Repeat booking">
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8a5 5 0 0 1 8.5-3.5L13 6M13 3v3h-3M13 8a5 5 0 0 1-8.5 3.5L3 10M3 13v-3h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                )}
                {refundMark && (density === 'full' || density === 'standard') && !event.redacted && (
                  <span style={REFUND_MARK_STYLE}>{refundMark.label}</span>
                )}
                {event.isServiceSegment && density !== 'micro' && (
                  <span className="dv2-event-segment">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2.5 1H7.5M2.5 9H7.5M3 1V3.5L5 5L3 6.5V9M7 1V3.5L5 5L7 6.5V9" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    ({event.segmentIndex + 1}/{event.segmentCount})
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        {overflow > 0 && (
          <div className="dv2-event-overflow">+{overflow}</div>
        )}
        {event.resizable && height >= 30 && (
          <>
            <div className="dv2-resize-handle dv2-resize-handle--top" data-edge="top" onMouseDown={handleResizeDown} onTouchStart={handleResizeDown} />
            <div className="dv2-resize-handle dv2-resize-handle--bottom" data-edge="bottom" onMouseDown={handleResizeDown} onTouchStart={handleResizeDown} />
          </>
        )}
      </div>
    );
  }

  // ── Event Card ──
  if (event.type === 'event') {
    const eventEmoji = event.emoji || matchEventEmoji(event.title);
    const blocksBookings = event.isBookingAllowed === false;
    // Default event background is charcoal; without the dark-bg modifier the
    // title/time stayed dark grey and the block read as a solid block of
    // colour. Same luminance detection used on booking blocks.
    const eventBg = event.diaryColour;
    const isEventDarkBg = eventBg ? getTextColour(eventBg) === 'light' : true;
    return (
      <div
        data-event-id={event.eventId || undefined}
        className={`dv2-event-block dv2-event--event ${landed ? 'dv2-event--landed' : ''} ${isActivated ? 'dv2-event--hold-ready' : ''} dv2-event--${density} ${isEventDarkBg ? 'dv2-event--dark-bg' : ''}`}
        style={style}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
      >
        <div className="dv2-event-color-strip dv2-event-color-strip--dark" />
        <div className="dv2-event-content">
          <span className="dv2-event-title">{event.title}</span>
          {density !== 'micro' && (
            <span className="dv2-event-time">{formatTime(effectiveStart)}-{formatTime(effectiveEnd)}</span>
          )}
        </div>
        {eventEmoji && density !== 'micro' && (
          <span className="dv2-event-emoji">{eventEmoji}</span>
        )}
        {/* "Not bookable" marker — bottom-left so it doesn't fight with the
            emoji in the opposite corner. Only on non-micro blocks; on micro
            (<22px) the block is too small to carry any extra detail. */}
        {blocksBookings && density !== 'micro' && (
          <span className="dv2-event-noblock" aria-label="Not available for booking">
            <BlockIcon />
          </span>
        )}
        {event.resizable && height >= 30 && (
          <>
            <div className="dv2-resize-handle dv2-resize-handle--top" data-edge="top" onMouseDown={handleResizeDown} onTouchStart={handleResizeDown} />
            <div className="dv2-resize-handle dv2-resize-handle--bottom" data-edge="bottom" onMouseDown={handleResizeDown} onTouchStart={handleResizeDown} />
          </>
        )}
      </div>
    );
  }

  // ── Break Card ──
  if (event.type === 'break') {
    return (
      <div
        className={`dv2-event-block dv2-event--break dv2-event--${density}`}
        style={style}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        <div className="dv2-event-content">
          <span className="dv2-event-title">Break</span>
          {density !== 'micro' && (
            <span className="dv2-event-time">{formatTime(effectiveStart)}-{formatTime(effectiveEnd)}</span>
          )}
        </div>
      </div>
    );
  }

  // ── Holiday Card ──
  if (event.type === 'holiday') {
    const holEmoji = event.emoji || matchEventEmoji(event.title);
    // Repeat title+emoji every 4 hours so it's visible while scrolling
    const repeatCount = Math.max(1, Math.floor((effectiveEnd - effectiveStart) / 240));
    return (
      <div
        data-event-id={event.eventId || undefined}
        className={`dv2-event-block dv2-event--holiday ${landed ? 'dv2-event--landed' : ''} dv2-event--${density}`}
        style={style}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        {Array.from({ length: repeatCount }, (_, idx) => (
          <div key={idx} className="dv2-event-content dv2-event-content--holiday dv2-event-content--holiday-repeat">
            {holEmoji && <span className="dv2-holiday-emoji">{holEmoji}</span>}
            <span className="dv2-event-title">{event.title}</span>
          </div>
        ))}
      </div>
    );
  }

  return null;
}

export default React.memo(EventBlock);
